import React from "react";
import { Link } from "react-router-dom";
import { PenTool, Leaf, Compass, Layers, ArrowRight } from "lucide-react";
import useSEO from "../hooks/useSEO";

const HERO =
  "/images/1aoh2udl-chatgpt-image-2-juin-2026-a-16-59-30.webp";

const PROD_IMG =
  "/images/lkpp1xg9-img-6024-2.webp";

export default function APropos() {
  useSEO({
    title: "À propos — Habitat bois haut de gamme",
    description:
      "YONO, marque partenaire d'Ideal Tiny, conçoit des habitats bois CLT haut de gamme pour les professionnels de l'hébergement. Architecture sobre, matériaux biosourcés, fabrication française près de Nantes.",
    path: "/a-propos",
    keywords: "YONO entreprise, fabricant habitat bois France, Ideal Tiny",
  });

  return (
    <div data-testid="apropos-page">
      {/* Hero with full-width image + overlaid title */}
      <section className="relative w-full" style={{ height: "70vh", minHeight: 520 }}>
        <img src={HERO} alt="YONO habitat bois" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
        <div className="relative h-full container-yono flex flex-col justify-end pb-10 lg:pb-12 text-white">
          <div className="eyebrow text-white/80 mb-4">À propos</div>
          <h1 className="font-heading text-[24px] sm:text-[34px] lg:text-[44px] tracking-tight leading-[1.05] max-w-3xl">
            À propos de YONO
          </h1>
          <p className="mt-6 text-[15px] sm:text-[16px] leading-[1.7] max-w-2xl text-white/90">
            Une marque d'habitats bois haut de gamme, portée par l'architecture,
            l'usage et la qualité du produit — déclinée pour les opérateurs
            professionnels.
          </p>
        </div>
      </section>

      {/* Notre vision - 2 column layout (heading left, text right) */}
      <section className="section">
        <div className="container-yono grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-5">Notre vision</div>
            <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[34px] tracking-tight leading-[1.1]">
              Un habitat bois pensé pour durer
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-[15px] sm:text-[16px] leading-[1.85] text-[var(--yono-soft-black)]/90">
            <p>
              YONO est une marque partenaire d'Ideal Tiny, fabricant d'habitats
              bois implanté près de Nantes.
            </p>
            <p>
              Nous développons des habitats bois innovants, conçus avec une
              exigence particulière de qualité, de confort et de durabilité.
            </p>
            <p>
              Notre conviction est simple : un espace bien dessiné, bien
              construit et bien proportionné crée plus de valeur d'usage, plus
              de désirabilité et une expérience plus juste — pour les
              voyageurs comme pour les exploitants.
            </p>
            <p>
              Nous concevons des produits bois haut de gamme qui associent
              qualité architecturale, matériaux biosourcés, intelligence des
              plans et cohérence constructive. Chaque habitat est pensé pour
              offrir un espace compact, chaleureux et parfaitement maîtrisé, où
              chaque choix répond à un usage réel.
            </p>
            <p>
              Chez YONO, nous défendons une vision sobre, durable et compétitive
              de l'habitat bois : conjuguer esthétique, performance, confort et
              maîtrise économique, sans jamais renoncer à la qualité.
            </p>
          </div>
        </div>
      </section>

      {/* Nos valeurs - beige background with 4 cards */}
      <section className="section bg-[var(--yono-beige)]">
        <div className="container-yono">
          <div className="eyebrow mb-6">Nos valeurs</div>
          <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.15] max-w-3xl mb-14">
            Quatre piliers qui guident notre approche du modulaire bois
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border border-[var(--yono-border)] bg-[var(--yono-border)] gap-px">
            {[
              {
                icon: PenTool,
                t: "Qualité architecturale",
                d: "Une attention particulière portée au dessin, aux proportions, aux matériaux et à la qualité des finitions.",
              },
              {
                icon: Leaf,
                t: "Matériaux biosourcés",
                d: "Une conception qui privilégie le bois et les matériaux biosourcés pour proposer des espaces plus durables, plus sains et plus cohérents.",
              },
              {
                icon: Compass,
                t: "Sur-mesure maîtrisé",
                d: "Pour les projets spécifiques, une capacité à concevoir des réponses adaptées sans perdre en lisibilité ni en qualité.",
              },
              {
                icon: Layers,
                t: "Bois et performance",
                d: "Le CLT et le bois massif comme base constructive, pour associer solidité, qualité d'usage et expression architecturale.",
              },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-[var(--yono-beige)] p-8 lg:p-10">
                <Icon
                  size={28}
                  strokeWidth={1.3}
                  className="text-[var(--yono-wood)] mb-6"
                />
                <h3 className="font-heading text-xl mb-3">{t}</h3>
                <p className="text-[14px] leading-relaxed text-[var(--yono-warm-grey)]">
                  {d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approche YONO - Production with image */}
      <section className="section">
        <div className="container-yono grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <div className="eyebrow mb-6">Approche YONO</div>
            <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.15]">
              Production
            </h2>
            <div className="mt-7 space-y-5 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/90">
              <p>
                YONO s'appuie sur Ideal Tiny, partenaire industriel exclusif de
                la marque, pour la fabrication de ses habitats bois.
              </p>
              <p>
                Implantée près de Nantes depuis 7 ans, Ideal Tiny réunit un
                atelier spécialisé dans la construction bois, une équipe d'une
                vingtaine de personnes et un bureau d'étude interne.
              </p>
              <p>
                Ce partenariat associe exigence de conception, maîtrise
                technique et savoir-faire industriel, afin de garantir une
                fabrication rigoureuse et constante.
              </p>
              <p>
                Chaque habitat YONO est ainsi pensé pour être fiable, durable,
                bien construit et reproductible.
              </p>
            </div>
          </div>
          <div className="aspect-[4/3] overflow-hidden border border-[var(--yono-border)]">
            <img src={PROD_IMG} alt="Production YONO" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Notre équipe */}
      <section className="section bg-[var(--yono-beige)]">
        <div className="container-yono">
          <div className="eyebrow mb-6">Notre équipe</div>
          <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] tracking-tight leading-[1.15] max-w-3xl mb-4">
            Architecture, ingénierie bois, production et accompagnement
          </h2>
          <p className="text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/80 max-w-2xl mb-12">
            YONO réunit des compétences en architecture, ingénierie bois,
            production industrielle et gestion de projets modulaires.
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px border border-[var(--yono-border)] bg-[var(--yono-border)]">
            {["Architecture", "Ingénierie bois", "Construction", "Accompagnement client de A à Z"].map(
              (e, i) => (
                <div key={e} className="bg-[var(--yono-beige)] p-8 lg:p-10">
                  <div className="font-mono text-[11px] tracking-[0.12em] text-[var(--yono-wood)] mb-3">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-heading text-lg">{e}</div>
                </div>
              )
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-yono">
          <div className="border border-[var(--yono-border)] p-12 lg:p-20 text-center">
            <h3 className="font-heading text-[26px] lg:text-[34px] tracking-tight">
              Travaillons ensemble
            </h3>
            <p className="mt-5 max-w-2xl mx-auto text-[var(--yono-warm-grey)] text-[15px]">
              Vous portez un projet d'hébergement ou de logement modulaire ?
              Échangeons sur vos ambitions et explorons comment YONO peut vous
              accompagner.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="btn-primary">
                Nous contacter <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/gamme" className="btn-outline">Voir la gamme</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
